import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, ease as mmEase } from "@/lib/motion";
import { Mail, ArrowRight, ArrowLeft, CheckCircle2, Lock, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";
import AuthThreadMini from "@/components/auth/AuthThreadMini";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ez = mmEase.expo;
const fv = (delay) => ({
  initial: { opacity: 0, y: 12, filter: "blur(3px)" },
  animate: { opacity: 1, y: 0,  filter: "blur(0px)" },
  transition: { duration: 0.48, ease: ez, delay },
});

// Password reset is code-based, not link-based: POST /auth/password-reset/request
// emails a 6-digit code (see backend/email_utils.py send_password_reset_email),
// then POST /auth/password-reset/confirm consumes it. This mirrors the Flutter
// app's ForgotPasswordScreen exactly. The API field is named "token" for
// historical reasons but holds the same 6-digit code shown in the email —
// it is not a URL token.
export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step,          setStep         ] = useState("request"); // "request" | "confirm" | "done"
  const [email,         setEmail        ] = useState("");
  const [code,          setCode         ] = useState("");
  const [newPassword,   setNewPassword  ] = useState("");
  const [showPassword,  setShowPassword ] = useState(false);
  const [loading,       setLoading      ] = useState(false);
  const [error,         setError        ] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone,    setResendDone   ] = useState(false);

  async function requestCode(e) {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim() });
      setStep("confirm");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send the reset code. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function confirmReset(e) {
    e.preventDefault();
    const trimmedCode = code.replace(/\s/g, "");
    if (trimmedCode.length !== 6) {
      setError("Please enter the 6-digit code from your email.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true); setError(null);
    try {
      await api.post("/auth/password-reset/confirm", {
        email: email.trim(),
        token: trimmedCode,
        new_password: newPassword,
      });
      setStep("done");
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid or expired code. Request a new one below.");
    } finally {
      setLoading(false);
    }
  }

  async function resend() {
    if (resendLoading) return;
    setResendLoading(true);
    setResendDone(false);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim() });
      setResendDone(true);
      setCode("");
      toast.success("New code sent — check your inbox.");
      setTimeout(() => setResendDone(false), 4000);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        "Could not resend the code. Check your connection and try again."
      );
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* ── Left — brand panel ───────────────────────────────── */}
      <div className="hidden lg:flex w-[440px] xl:w-[500px] flex-col hero-gradient grain relative overflow-hidden">
        <div
          className="absolute top-[20%] left-[10%] w-64 h-64 rounded-full blur-3xl animate-orb pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.16) 0%, transparent 70%)" }}
        />
        <div
          className="absolute bottom-[18%] right-[8%] w-48 h-48 rounded-full blur-3xl animate-orb-b pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(6,182,212,0.10) 0%, transparent 70%)" }}
        />

        <div className="relative z-10 p-10 flex flex-col h-full">
          <Link to="/"><Logo variant="dark" size="md" /></Link>

          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: ez }}
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-4">
                Password reset
              </p>
              <h1
                className="font-display font-black text-white leading-[1.08] text-balance mb-5"
                style={{ fontSize: "clamp(1.7rem, 2.8vw, 2.4rem)", letterSpacing: "-0.04em" }}
              >
                We've got you covered.
              </h1>
              <p className="text-white/48 text-[14px] leading-relaxed max-w-sm">
                Enter your email address and we'll send you a 6-digit code to reset your password. It takes less than a minute.
              </p>
            </motion.div>

            <motion.div
              className="mt-12 space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.55, delay: 0.28, ease: ez }}
            >
              {[
                { icon: "🔒", text: "Reset code expires in 1 hour" },
                { icon: "📧", text: "Check your spam folder if it doesn't arrive" },
                { icon: "🛡️", text: "Code is single-use and fully encrypted" },
              ].map(({ icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <span className="text-base">{icon}</span>
                  <span className="text-[13px] text-white/50">{text}</span>
                </div>
              ))}
            </motion.div>

            {/* Thread */}
            <motion.div
              className="mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.42, ease: ez }}
            >
              <AuthThreadMini color="var(--color-success)" />
            </motion.div>
          </div>

          <div className="flex items-center gap-5 mb-5">
            <div className="flex items-center gap-1.5">
              <Lock size={12} className="text-emerald" strokeWidth={2.5} />
              <span className="text-[10px] font-semibold text-white/38">Bank-grade Encryption</span>
            </div>
          </div>

          <p className="text-white/22 text-[11px]">© {new Date().getFullYear()} MoneyMeet Pay</p>
        </div>
      </div>

      {/* ── Right — form / success ─────────────────────────── */}
      <div className="flex-1 flex items-center justify-center bg-white px-5 py-12">
        <div className="w-full max-w-[380px]">
          <div className="lg:hidden mb-8">
            <Link to="/"><Logo variant="light" size="md" /></Link>
          </div>

          {step === "request" && (
            <>
              <motion.div {...fv(0)}>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground hover:text-navy transition-colors mb-8"
                >
                  <ArrowLeft size={13} /> Back to sign in
                </Link>
                <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-2">
                  Recover access
                </p>
                <h2
                  className="font-display font-black text-navy tracking-tight mb-1"
                  style={{ fontSize: "1.65rem", letterSpacing: "-0.035em" }}
                >
                  Reset your password
                </h2>
                <p className="text-sm text-muted-foreground mb-8">
                  We'll email you a 6-digit code to reset your password.
                </p>
              </motion.div>

              <form onSubmit={requestCode} className="space-y-4">
                <motion.div {...fv(0.08)}>
                  <label className="input-label" htmlFor="fp-email">Email address</label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      id="fp-email"
                      data-testid="forgot-email-input"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="input-field pl-9"
                    />
                  </div>
                </motion.div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
                    role="alert"
                  >
                    {error}
                  </motion.div>
                )}

                <motion.div {...fv(0.14)}>
                  <button
                    type="submit"
                    data-testid="forgot-submit-button"
                    disabled={loading || !email}
                    className="btn-primary w-full justify-center py-3.5 text-sm mt-1 disabled:opacity-55 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                        Sending…
                      </span>
                    ) : (
                      <>Send reset code <ArrowRight size={14} /></>
                    )}
                  </button>
                </motion.div>
              </form>
            </>
          )}

          {step === "confirm" && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.48, ease: ez }}>
              <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-2">
                Check your inbox
              </p>
              <h2
                className="font-display font-black text-navy tracking-tight mb-1"
                style={{ fontSize: "1.65rem", letterSpacing: "-0.035em" }}
              >
                Enter your reset code
              </h2>
              <p className="text-sm text-muted-foreground mb-8">
                We sent a 6-digit code to <span className="font-semibold text-navy">{email}</span>. Enter it below along with your new password.
              </p>

              <form onSubmit={confirmReset} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <label className="input-label" htmlFor="fp-code">Reset code</label>
                  <input
                    id="fp-code"
                    data-testid="reset-code-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(e) => { setCode(e.target.value.replace(/[^0-9]/g, "")); setError(null); }}
                    placeholder="123456"
                    className="input-field text-center text-lg font-bold tracking-[0.4em]"
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="fp-new-password">New password</label>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      id="fp-new-password"
                      data-testid="reset-new-password-input"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
                      placeholder="At least 8 characters"
                      className="input-field pl-9 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy transition-colors"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
                    role="alert"
                  >
                    {error}
                  </motion.div>
                )}

                <button
                  type="submit"
                  data-testid="reset-confirm-button"
                  disabled={loading || code.length !== 6 || newPassword.length < 8}
                  className="btn-primary w-full justify-center py-3.5 text-sm mt-1 disabled:opacity-55 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                      Resetting…
                    </span>
                  ) : (
                    <>Reset password <ArrowRight size={14} /></>
                  )}
                </button>
              </form>

              <p className="text-[12px] text-muted-foreground mt-5 text-center">
                Didn't receive it? Check spam, or{" "}
                <button
                  type="button"
                  data-testid="reset-resend-button"
                  onClick={resend}
                  disabled={resendLoading || resendDone}
                  className="font-semibold text-emerald hover:text-emerald-hover transition-colors disabled:opacity-50"
                >
                  {resendLoading ? "Resending…" : resendDone ? "✓ Resent" : "resend the code."}
                </button>
              </p>

              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold text-muted-foreground hover:text-navy transition-colors mt-4 w-full"
              >
                <ArrowLeft size={12} /> Back to sign in
              </Link>
            </motion.div>
          )}

          {step === "done" && (
            /* ── Success state ── */
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.55, ease: ez }}
              className="text-center"
              data-testid="forgot-success"
            >
              <div
                className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-6"
                style={{
                  background: "rgba(16,185,129,0.10)",
                  boxShadow: "0 8px 32px rgba(16,185,129,0.22)",
                }}
              >
                <CheckCircle2 size={28} className="text-emerald" strokeWidth={2} />
              </div>

              <h2
                className="font-display font-black text-navy mb-3"
                style={{ fontSize: "1.55rem", letterSpacing: "-0.03em" }}
              >
                Password updated
              </h2>
              <p className="text-[14px] text-muted-foreground leading-relaxed mb-8">
                Taking you to sign in…
              </p>

              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground hover:text-navy transition-colors"
              >
                <ArrowLeft size={12} /> Back to sign in
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
